// ── CDC SPEND PLAN TAB ───────────────────────────────────────
// One month's work, in the order a grants officer does it:
//   1. choose the month's folder of spend plans and enter the rate;
//   2. check what NofoDesk read from each file, and the lines it flagged;
//   3. check TX_CURR (it changes monthly), and confirm any Tx split;
//   4. download the sheet and paste it under the last month in the CDC workbook.
// The rules live on the server (lib/cdcSources.js, lib/cdcSpendPlan.js).
import { useState } from "react";
import { FONT_UI, FONT_MONO } from "../theme";
import { API, apiFetch } from "../utils/api";

const KIND_LABEL = {
  "admin-category": "Admin plan", "admin-memo": "Admin plan", department: "Department plan",
  program: "Program workplan", "state-rates": "TX_CURR (state rates)",
};
const SPLIT_STATES = ["Abuja FCT", "Katsina", "Nasarawa", "Rivers"];
const TX_KEY = "nofodesk_cdc_txcurr";
const AMBER = "#d97706", RED = "#dc2626", GREEN = "#059669";

const loadTx = () => { try { return JSON.parse(localStorage.getItem(TX_KEY) || "null"); } catch { return null; } };
const saveTx = (v) => { try { localStorage.setItem(TX_KEY, JSON.stringify(v)); } catch { /* storage blocked */ } };
const usd = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();
const ngn = (n) => "₦" + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const rateNum = (s) => Number(String(s || "").replace(/[^\d.]/g, ""));

function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

// A folder upload names every file "Sept 2026/Rivers/…". The shared top
// folder says nothing about state or department, so it is dropped.
function withPaths(list) {
  const picked = Array.from(list || []).filter((f) => /\.xls[xm]?$/i.test(f.name));
  const paths = picked.map((f) => (f.webkitRelativePath || f.name).replace(/\\/g, "/"));
  const roots = new Set(paths.map((p) => (p.includes("/") ? p.split("/")[0] : "")));
  const root = roots.size === 1 ? [...roots][0] : "";
  return picked.map((file, i) => ({ file, path: root ? paths[i].slice(root.length + 1) : paths[i] }));
}

export default function CdcSpendPlanTab({ T, card, label, input, btn }) {
  const [month, setMonth]         = useState("");
  const [rate, setRate]           = useState("");
  const [files, setFiles]         = useState([]);
  const [parsed, setParsed]       = useState(null);
  const [tx, setTx]               = useState(null);
  const [preview, setPreview]     = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult]       = useState(null);
  const [problems, setProblems]   = useState(null);
  const [busy, setBusy]           = useState("");
  const [error, setError]         = useState("");

  const th   = { textAlign: "left", padding: "7px 8px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.06em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td   = { padding: "7px 8px", fontFamily: FONT_UI, fontSize: 12.5, color: T.textBody, borderBottom: `1px solid ${T.border}`, verticalAlign: "top" };
  const num  = { ...td, textAlign: "right", fontFamily: FONT_MONO, whiteSpace: "nowrap" };
  const note = (c) => ({ padding: "10px 12px", borderRadius: 8, fontFamily: FONT_UI, fontSize: 12.5, lineHeight: 1.55, background: `${c}14`, border: `1px solid ${c}55`, color: T.textBody, marginTop: 10 });
  const plain = { padding: "10px 12px", borderRadius: 8, fontFamily: FONT_UI, fontSize: 12.5, lineHeight: 1.55, border: `1px solid ${T.border}`, color: T.textMuted, marginTop: 10 };
  const title = { fontFamily: FONT_UI, fontSize: 15, fontWeight: 700, color: T.text, margin: "0 0 4px" };
  const sub   = { fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, margin: "0 0 14px", lineHeight: 1.55 };

  const clearResults = () => { setPreview(null); setConfirmed(false); setResult(null); setProblems(null); };
  const reset = () => { setParsed(null); clearResults(); setError(""); };
  const pick = (list) => {
    const add = withPaths(list);
    setFiles((f) => { const seen = new Set(f.map((x) => x.path)); return [...f, ...add.filter((x) => !seen.has(x.path))]; });
    reset();
  };

  const readFiles = async () => {
    reset(); setBusy("read");
    try {
      const fd = new FormData();
      files.forEach(({ file, path }) => fd.append("files", file, path));
      fd.append("month", month);
      const r = await apiFetch(`${API}/cdc/parse`, { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Server error ${r.status}`);
      setParsed(d);
      // TX_CURR: a rates file in this upload wins; otherwise the last figures
      // used, flagged, because TX_CURR changes month to month.
      if (d.rates?.states?.length) {
        const src = d.sources.find((s) => s.kind === "state-rates")?.file || "uploaded file";
        setTx({ month, source: src, states: d.rates.states.map((s) => ({ state: s.state, tx: String(s.tx) })) });
      } else {
        const prev = loadTx();
        setTx(prev ? { ...prev, reused: true } : { month: "", source: "", states: SPLIT_STATES.map((state) => ({ state, tx: "" })) });
      }
    } catch (e) { setError(e.message); }
    setBusy("");
  };

  const txRates = () => {
    const s = (tx?.states || []).filter((x) => Number(x.tx) > 0).map((x) => ({ state: x.state, tx: Number(x.tx) }));
    return s.length ? s : undefined;
  };
  const body = (confirmSplit) => JSON.stringify({ items: parsed.items, month, rate: rateNum(rate), rates: txRates(), confirmSplit });

  const compile = async (confirmSplit) => {
    setBusy("compile"); setError(""); setProblems(null); setResult(null);
    try {
      const r = await apiFetch(`${API}/cdc/compile`, { method: "POST", headers: { "Content-Type": "application/json" }, body: body(confirmSplit) });
      const d = await r.json().catch(() => ({}));
      if (r.status === 409) { setPreview(d.preview); setConfirmed(false); }
      else if (r.status === 422) setProblems(d);
      else if (!r.ok) throw new Error(d.error || `Server error ${r.status}`);
      else {
        setResult(d.result); setConfirmed(confirmSplit);
        if (txRates()) saveTx({ month, source: tx.source || "typed in", states: tx.states });
      }
    } catch (e) { setError(e.message); }
    setBusy("");
  };

  const download = async () => {
    setBusy("download"); setError("");
    try {
      const r = await apiFetch(`${API}/cdc/compile?format=xlsx`, { method: "POST", headers: { "Content-Type": "application/json" }, body: body(confirmed) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || d.errors?.[0]?.error || `Server error ${r.status}`); }
      saveFile(await r.blob(), `CDC Spend Plan ${result.month}.xlsx`);
    } catch (e) { setError(e.message); }
    setBusy("");
  };

  const setTxValue = (i, v) => {
    setTx((t) => ({ ...t, reused: false, source: "edited here", states: t.states.map((x, j) => (j === i ? { ...x, tx: v.replace(/[^\d]/g, "") } : x)) }));
    clearResults();
  };
  const txTotal = (tx?.states || []).reduce((a, s) => a + (Number(s.tx) || 0), 0);
  const canRead = files.length > 0 && !busy;
  const ready = Boolean(parsed && month.trim() && rateNum(rate) > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* 1 — inputs */}
      <div style={card}>
        <div style={title}>1 · This month's spend plans</div>
        <p style={sub}>Choose the month's folder: every state and department plan, plus the state rates (TX_CURR) file if it changed. NofoDesk tells each file apart by what is inside it, so file and folder names don't matter.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 14 }}>
          <div><label style={label}>Spend plan month</label>
            <input style={input} value={month} placeholder="September 2026" onChange={(e) => { setMonth(e.target.value); clearResults(); }} /></div>
          <div><label style={label}>Rate (₦ to $1)</label>
            <input style={input} value={rate} inputMode="decimal" placeholder="1361" onChange={(e) => { setRate(e.target.value); clearResults(); }} /></div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ ...btn(false), display: "inline-flex", alignItems: "center" }}>Choose folder
            <input type="file" multiple webkitdirectory="" directory="" hidden onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
          </label>
          <label style={{ ...btn(false), display: "inline-flex", alignItems: "center" }}>Add files
            <input type="file" multiple accept=".xlsx,.xlsm,.xls" hidden onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
          </label>
          {files.length > 0 && <button style={{ ...btn(false), border: "none" }} onClick={() => { setFiles([]); reset(); }}>Clear</button>}
          <span style={{ flex: 1 }} />
          <button style={{ ...btn(true), opacity: canRead ? 1 : 0.5 }} disabled={!canRead} onClick={readFiles}>
            {busy === "read" ? "Reading… large plans can take a minute" : `Read ${files.length || ""} file${files.length === 1 ? "" : "s"}`}
          </button>
        </div>
        {files.length > 0 && !parsed && (
          <div style={{ marginTop: 12, fontFamily: FONT_MONO, fontSize: 11.5, color: T.textMuted, lineHeight: 1.7 }}>
            {files.map((f) => <div key={f.path}>{f.path}</div>)}
          </div>
        )}
        {error && <div style={note(RED)}>{error}</div>}
      </div>

      {/* 2 — what was read */}
      {parsed && (
        <div style={card}>
          <div style={title}>2 · What NofoDesk read</div>
          <p style={sub}>Check each file was read as the right kind of plan and state. Jos counts as Nasarawa; Central is Head Office; department plans carry their own state columns.</p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>File</th><th style={th}>Read as</th><th style={th}>State</th><th style={th}>Dept</th>
                <th style={{ ...th, textAlign: "right" }}>Lines</th><th style={{ ...th, textAlign: "right" }}>Naira</th>
              </tr></thead>
              <tbody>{parsed.sources.map((s) => (
                <tr key={s.file}>
                  <td style={td}>{s.file}</td>
                  <td style={td}>{KIND_LABEL[s.kind] || s.kind}</td>
                  <td style={td}>{s.state || (s.kind === "department" ? "by row" : "—")}</td>
                  <td style={td}>{s.department || "—"}</td>
                  <td style={num}>{s.lines}</td>
                  <td style={num}>{s.ngn != null ? ngn(s.ngn) : "—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          {parsed.warnings?.length > 0 && (
            <div style={note(AMBER)}>{parsed.warnings.map((w, i) => <div key={i}>• {w}</div>)}</div>
          )}
          {parsed.ignored?.length > 0 && (
            <div style={plain}><b>Not used:</b>{parsed.ignored.map((x, i) => <div key={i}>• {x.file}: {x.reason}</div>)}</div>
          )}
          {parsed.review?.length > 0 && (
            <>
              <div style={{ ...title, fontSize: 13.5, marginTop: 18 }}>Lines to check ({parsed.review.length})</div>
              <p style={{ ...sub, marginBottom: 8 }}>Included as shown. These are judgement calls a preparer would normally make: confirm them before submitting.</p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={th}>File</th><th style={th}>Line</th><th style={th}>Goes to</th><th style={{ ...th, textAlign: "right" }}>Naira</th><th style={th}>Why</th></tr></thead>
                  <tbody>{parsed.review.map((it, i) => (
                    <tr key={i}>
                      <td style={td}>{it.source}</td><td style={td}>{it.description}</td><td style={td}>{it.group}{it.state ? ` · ${it.state}` : ""}</td>
                      <td style={num}>{ngn(it.ngn)}</td><td style={{ ...td, color: AMBER }}>{it.review}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* 3 — TX_CURR */}
      {parsed && tx && (
        <div style={card}>
          <div style={title}>3 · TX_CURR by state</div>
          <p style={sub}>Used only for department lines that name no state (usually SI, PCU, PCT, Procurement): those are shared across the four states in proportion to TX_CURR. TX_CURR changes monthly, so check these are this month's figures.</p>
          {tx.reused && (
            <div style={{ ...note(AMBER), marginTop: 0, marginBottom: 12 }}>
              No state rates file in this upload, so these are the figures last used{tx.month ? ` for ${tx.month}` : ""}{tx.source ? ` (${tx.source})` : ""}. Update them if TX_CURR changed.
            </div>
          )}
          {!tx.reused && tx.source && <div style={{ ...sub, marginTop: -8 }}>From {tx.source}.</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            {tx.states.map((s, i) => (
              <div key={s.state}>
                <label style={label}>{s.state}{txTotal > 0 && Number(s.tx) > 0 ? ` · ${((Number(s.tx) / txTotal) * 100).toFixed(1)}%` : ""}</label>
                <input style={input} inputMode="numeric" value={s.tx} placeholder="TX_CURR" onChange={(e) => setTxValue(i, e.target.value)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 4 — compile */}
      {parsed && (
        <div style={card}>
          <div style={title}>4 · Compile</div>
          <p style={sub}>Collates every line into CDC's lines and converts at ₦{rateNum(rate) ? rateNum(rate).toLocaleString() : "—"} = $1. If any line needs the Tx split, you'll see the split first. Nothing is split without your confirmation.</p>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button style={{ ...btn(true), opacity: ready && !busy ? 1 : 0.5 }} disabled={!ready || !!busy} onClick={() => compile(false)}>
              {busy === "compile" ? "Compiling…" : "Compile spend plan"}
            </button>
            {!ready && <span style={{ ...sub, margin: 0 }}>Enter the month and rate above first.</span>}
          </div>

          {problems?.errors?.length > 0 && (
            <div style={note(RED)}>
              {problems.errors.map((e, i) => (
                <div key={i} style={{ marginBottom: 4 }}>• {e.error}
                  {e.lines?.length > 0 && <div style={{ paddingLeft: 14 }}>{e.lines.slice(0, 12).map((l, j) => <div key={j}>{l.description} ({l.department || "no department"}) {ngn(l.ngn)}</div>)}</div>}
                </div>
              ))}
            </div>
          )}

          {preview?.split?.length > 0 && !result && (
            <div style={{ marginTop: 16 }}>
              <div style={{ ...title, fontSize: 13.5 }}>Tx split to confirm ({preview.split.length} line{preview.split.length === 1 ? "" : "s"})</div>
              <p style={{ ...sub, marginBottom: 8 }}>These lines name no state, so they are shared by TX_CURR. Amounts are in naira and add up exactly to each line.</p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr>
                    <th style={th}>Line</th><th style={th}>Dept</th><th style={{ ...th, textAlign: "right" }}>Naira</th>
                    {Object.keys(preview.split[0].split).map((s) => <th key={s} style={{ ...th, textAlign: "right" }}>{s}</th>)}
                  </tr></thead>
                  <tbody>{preview.split.map((l, i) => (
                    <tr key={i}>
                      <td style={td}>{l.description}</td><td style={td}>{l.department}</td><td style={num}>{ngn(l.ngn)}</td>
                      {Object.values(l.split).map((v, j) => <td key={j} style={num}>{ngn(v)}</td>)}
                    </tr>
                  ))}</tbody>
                </table>
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button style={btn(true)} disabled={!!busy} onClick={() => compile(true)}>{busy === "compile" ? "Compiling…" : "Confirm split and compile"}</button>
                <button style={btn(false)} disabled={!!busy} onClick={() => setPreview(null)}>Not yet</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 5 — result */}
      {result && (
        <div style={card}>
          <div style={title}>{result.month} spend plan</div>
          <p style={{ ...sub, marginBottom: 10 }}>
            {result.lines.length} lines · {usd(result.grandTotalUsd)} at ₦{Number(result.rate).toLocaleString()} = $1 ·{" "}
            {result.reconciles
              ? <span style={{ color: GREEN, fontWeight: 600 }}>every naira accounted for</span>
              : <span style={{ color: RED, fontWeight: 600 }}>totals don't reconcile: check the Working sheet in the download</span>}
            {confirmed && result.splitApplied?.length > 0 ? ` · Tx split applied to ${result.splitApplied.length} line${result.splitApplied.length === 1 ? "" : "s"}` : ""}
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={th}>Object class</th><th style={th}>Sub category</th><th style={th}>Description</th>
                {result.states.map((s) => <th key={s} style={{ ...th, textAlign: "right" }}>{s}</th>)}
                <th style={{ ...th, textAlign: "right" }}>Total $</th>
              </tr></thead>
              <tbody>
                {result.lines.map((l, i) => (
                  <tr key={i}>
                    <td style={td}>{l.objectClass}</td><td style={td}>{l.subCategory}</td>
                    <td style={{ ...td, maxWidth: 320 }} title={l.description}>{l.description.length > 110 ? `${l.description.slice(0, 110)}…` : l.description}</td>
                    {result.states.map((s) => <td key={s} style={num}>{l.usd[s] ? usd(l.usd[s]) : ""}</td>)}
                    <td style={{ ...num, fontWeight: 700 }}>{usd(l.totalUsd)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...td, fontWeight: 700 }} colSpan={3}>Total</td>
                  {result.states.map((s) => <td key={s} style={{ ...num, fontWeight: 700 }}>{usd(result.totals[s])}</td>)}
                  <td style={{ ...num, fontWeight: 700 }}>{usd(result.grandTotalUsd)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {result.missingRecurring?.length > 0 && (
            <div style={plain}>Usually in a monthly plan but not in these files: {result.missingRecurring.join(", ")}. Fine if this month doesn't include them.</div>
          )}
          <div style={{ display: "flex", gap: 12, marginTop: 16, alignItems: "center", flexWrap: "wrap" }}>
            <button style={btn(true)} disabled={!!busy} onClick={download}>{busy === "download" ? "Preparing…" : "Download for the CDC workbook (.xlsx)"}</button>
            <span style={{ ...sub, margin: 0, flex: 1, minWidth: 240 }}>
              Open it, copy rows 8 to {8 + result.lines.length} (columns B to M), and paste at column B of the first empty row under the last month in the CDC workbook.
            </span>
          </div>
          {error && <div style={note(RED)}>{error}</div>}
        </div>
      )}
    </div>
  );
}
